import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Box,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
} from '@mui/material';
import { createGuestOrder, getShippingCost } from '../../services/api';
import { useNavigate } from 'react-router-dom';

interface CartItem {
  product: {
    id: number;
    name: string;
    price: number;
  };
  quantity: number;
}

interface GuestCheckoutProps {
  cart: CartItem[];
  onSuccess: () => void;
  onCancel: () => void;
  barberId: number;
}

const steps = ['Customer Information', 'Review Order', 'Complete'];

const GuestCheckout: React.FC<GuestCheckoutProps> = ({ cart, onSuccess, onCancel, barberId }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [shippingCost, setShippingCost] = useState(0);
  const navigate = useNavigate();
  
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });

  useEffect(() => {
    const fetchShippingCost = async () => {
      try {
        const cost = await getShippingCost(barberId);
        setShippingCost(cost);
      } catch (error) {
        console.error('Error fetching shipping cost:', error);
      }
    };
    
    fetchShippingCost();
  }, [barberId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const isCustomerInfoValid = () => {
    return (
      customerInfo.name.trim() !== '' &&
      customerInfo.email.trim() !== '' &&
      customerInfo.phone.trim() !== '' &&
      customerInfo.address.trim() !== ''
    );
  };

  const getSubtotal = () => {
    return cart.reduce((total, item) => total + item.product.price * item.quantity, 0);
  };

  const getTotalPrice = () => {
    return getSubtotal() + shippingCost;
  };

  const handleNext = () => {
    if (activeStep === 0 && !isCustomerInfoValid()) {
      setError('Please fill in all required fields');
      return;
    }
    
    setError(null);
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      setError(null);

      await createGuestOrder({
        items: cart.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity
        })),
        customer_info: customerInfo,
        barberId: barberId
      });

      setActiveStep(2);
      onSuccess();
    } catch (error: any) {
      console.error('Error creating order:', error);
      
      // Extract a meaningful error message
      let errorMessage = 'Failed to create order. Please try again.';
      
      if (error.response && error.response.data) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Handle validation errors array
          const validationErrors = error.response.data.detail;
          if (validationErrors.length > 0) {
            // Just show the first validation error for simplicity
            const firstError = validationErrors[0];
            errorMessage = `Validation error: ${firstError.msg} for ${firstError.loc.join('.')}`;
          }
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Customer Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={customerInfo.name}
                  onChange={handleInputChange}
                  error={error !== null && customerInfo.name.trim() === ''}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={customerInfo.email}
                  onChange={handleInputChange}
                  error={error !== null && customerInfo.email.trim() === ''}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={customerInfo.phone}
                  onChange={handleInputChange}
                  error={error !== null && customerInfo.phone.trim() === ''}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Shipping Address"
                  name="address"
                  multiline
                  rows={3}
                  value={customerInfo.address}
                  onChange={handleInputChange}
                  error={error !== null && customerInfo.address.trim() === ''}
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Order Summary
            </Typography>
            <List>
              {cart.map((item) => (
                <React.Fragment key={item.product.id}>
                  <ListItem>
                    <ListItemText
                      primary={item.product.name}
                      secondary={`$${item.product.price} x ${item.quantity}`}
                    />
                    <Typography variant="body1">
                      ${(item.product.price * item.quantity).toFixed(2)}
                    </Typography>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              <ListItem>
                <ListItemText primary="Subtotal" />
                <Typography variant="body1">
                  ${getSubtotal().toFixed(2)}
                </Typography>
              </ListItem>
              <ListItem>
                <ListItemText primary="Shipping" />
                <Typography variant="body1">
                  ${shippingCost.toFixed(2)}
                </Typography>
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText primary="Total" />
                <Typography variant="h6">
                  ${getTotalPrice().toFixed(2)}
                </Typography>
              </ListItem>
            </List>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Customer Information
              </Typography>
              <Typography>Name: {customerInfo.name}</Typography>
              <Typography>Email: {customerInfo.email}</Typography>
              <Typography>Phone: {customerInfo.phone}</Typography>
              <Typography>Address: {customerInfo.address}</Typography>
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Order Complete
            </Typography>
            <Typography paragraph>
              Thank you for your order! A confirmation has been sent to your email.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(`/barber/${barberId}`)}
            >
              Continue Shopping
            </Button>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom align="center">
          Checkout as Guest
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          {activeStep < 2 && (
            <>
              <Button
                onClick={onCancel}
                sx={{ mr: 1 }}
                disabled={loading}
              >
                Cancel
              </Button>
              {activeStep > 0 && (
                <Button
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                  disabled={loading}
                >
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                onClick={activeStep === 1 ? handleConfirm : handleNext}
                disabled={loading}
              >
                {activeStep === 1 ? 'Place Order' : 'Continue'}
              </Button>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default GuestCheckout; 